import React, { ReactNode, createContext, useContext } from "react";

export interface PromiseProps<T> {
  on: Promise<T>;
  children: ReactNode;
}

export interface PendingProps {
  children: ReactNode;
}

export interface ResolveProps<T> {
  children: (data: T) => ReactNode;
}

export interface RejectProps {
  children: (error: any) => ReactNode;
}

export interface PromiseState<T> {
  status: "pending" | "resolved" | "rejected";
  data?: T;
  error?: any;
}

const PromiseContext = createContext<PromiseState<any>>({
  status: "pending",
});

function PromiseBlock<T>({ on, children }: PromiseProps<T>) {
  const [state, setState] = React.useState<PromiseState<T>>({
    status: "pending",
    data: undefined,
    error: undefined,
  });

  React.useEffect(() => {
    on.then(
      (data) => {
        setState({ status: "resolved", data });
      },
      (error) => {
        setState({ status: "rejected", error });
      },
    );
  }, [on]);

  return <PromiseContext.Provider value={state}>{children}</PromiseContext.Provider>;
}

function Pending({ children }: PendingProps) {
  return <>{children}</>;
}

function Resolve<T>({ children }: ResolveProps<T>) {
  const { status, data } = useContext(PromiseContext);

  if (status === "resolved" && data !== undefined) {
    return <>{children(data)}</>;
  }

  return null;
}

function Reject({ children }: RejectProps) {
  const { status, error } = useContext(PromiseContext);

  if (status === "rejected" && error !== undefined) {
    return <>{children(error)}</>;
  }

  return null;
}

export { PromiseBlock, Pending, Resolve, Reject };
